import { useState } from 'react';
import type { AppV1Cfg, AppV1Row } from '@/lib/types/AppV1';
import { AppPreviewUtils } from '../AppPreviewUtils';

const { fhd, withAlpha } = AppPreviewUtils;

/** The shell's billboard zone: 1920x648 at FHD (cross-pinned with hero/v2 derivation). */
export const BILLBOARD_HEIGHT_FHD = 648;
/** The NavBar's opaque bg strip that overlays the zone's top. */
export const NAVBAR_HEIGHT_FHD = 120;

interface AppTvBillboardProps {
  cfg: AppV1Cfg;
  row: AppV1Row;
}

/**
 * Mirror of rokucore `HeroBillboard` (design: ott/design system/surfaces.md
 * "Hero billboard", adr_0002). Geometry is the shell's FHD layout expressed in
 * container-width units so the preview stays faithful at any frame size.
 *
 * - card: the customer's complete raw poster inset at [960,141] 864x486 over a
 *   blurred, darkened ambience of the same art (baked `ambient` when present).
 * - immersive: the band-composed derived hero at 0.92 scale ([77,-93] 1766x994)
 *   over a decode-blur backdrop, with 420px smoothstep edge melts.
 * Both: left/bottom scrims, metadata block lower-left (title, ≤3-line
 * description, CTA pill that stacks under the text), page dots beside the CTA.
 * The carousel is static here: dots switch the item, hovering simulates focus.
 */
export function AppTvBillboard({ cfg, row }: AppTvBillboardProps) {
  const { colors } = cfg.theme;
  const [idx, setIdx] = useState(0);
  const [focused, setFocused] = useState(false);

  const items = row.items;
  const item = items[Math.min(idx, items.length - 1)];
  const style = AppPreviewUtils.heroStyle(row);
  const immersive = style === 'immersive';

  const heroUrl = AppPreviewUtils.heroArtworkUrl(item);
  const ambientUrl = immersive
    ? heroUrl && AppPreviewUtils.sizedArtworkUrl(heroUrl, 320)
    : (item.artwork.ambient ?? (heroUrl && AppPreviewUtils.sizedArtworkUrl(heroUrl, 320)));
  const bakedAmbient = !immersive && Boolean(item.artwork.ambient);
  const cardUrl = heroUrl && AppPreviewUtils.sizedArtworkUrl(heroUrl, 640, true);
  const immersiveUrl = heroUrl && AppPreviewUtils.sizedArtworkUrl(heroUrl, 960);

  return (
    <div
      onMouseEnter={() => setFocused(true)}
      onMouseLeave={() => setFocused(false)}
      style={{
        position: 'relative',
        width: '100%',
        height: fhd(BILLBOARD_HEIGHT_FHD),
        overflow: 'hidden',
        backgroundColor: colors.bgPrimary,
      }}
    >
      {/* ambience / backdrop: the only layer allowed to bleed to the edges */}
      {ambientUrl && (
        <img
          src={ambientUrl}
          alt=""
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // baked ambient renders as-is; the fallback is the shell's decode-blur
            // + 0x303030@0.65 darkening
            filter: bakedAmbient ? undefined : 'blur(18px) brightness(0.45)',
            opacity: bakedAmbient ? 1 : 0.85,
            transform: 'scale(1.06)',
          }}
        />
      )}

      {/* immersive: band-composed hero at 0.92 scale, clipped to the zone */}
      {immersive && immersiveUrl && (
        <img
          src={immersiveUrl}
          alt={item.title}
          style={{
            position: 'absolute',
            left: fhd(77),
            top: fhd(-93),
            width: fhd(1766),
            height: fhd(994),
            objectFit: 'contain',
          }}
        />
      )}
      {immersive && (
        <>
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: fhd(420),
              height: '100%',
              background: AppPreviewUtils.edgeMeltGradient(colors.bgPrimary, 'to right'),
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: fhd(420),
              height: '100%',
              background: AppPreviewUtils.edgeMeltGradient(colors.bgPrimary, 'to left'),
            }}
          />
        </>
      )}

      {/* scrims (both styles): left 480 for the metadata, bottom 180 into the rows */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: fhd(480),
          height: '100%',
          background: `linear-gradient(to right, ${withAlpha(colors.bgPrimary, 0.85)}, ${withAlpha(colors.bgPrimary, 0)})`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: fhd(468),
          width: '100%',
          height: fhd(180),
          background: `linear-gradient(to bottom, ${withAlpha(colors.bgPrimary, 0)}, ${withAlpha(colors.bgPrimary, 0.9)})`,
        }}
      />

      {/* card: the complete poster, whole, inside the visible + safe region */}
      {!immersive && (
        <div
          style={{
            position: 'absolute',
            left: fhd(960),
            top: fhd(141),
            width: fhd(864),
            height: fhd(486),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {cardUrl ? (
            <img
              src={cardUrl}
              alt={item.title}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: colors.bgSurface,
                color: colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: fhd(28),
              }}
            >
              {item.title}
            </div>
          )}
        </div>
      )}

      {/* metadata block, lower-left inside the title-safe area */}
      <div
        style={{
          position: 'absolute',
          left: fhd(96),
          top: fhd(360),
          width: fhd(720),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            color: colors.textPrimary,
            fontSize: fhd(44),
            lineHeight: 1.2,
            fontWeight: 700,
            height: fhd(70),
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {item.title}
        </div>
        {item.descShort && (
          <div
            style={{
              marginTop: fhd(12),
              width: fhd(680),
              color: colors.textSecondary,
              fontSize: fhd(30),
              lineHeight: fhd(40),
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.descShort}
          </div>
        )}
        <div
          style={{
            marginTop: item.descShort ? fhd(20) : fhd(18),
            display: 'flex',
            alignItems: 'center',
            gap: fhd(24),
          }}
        >
          {/* CTA pill: accent while the billboard holds focus, quiet surface otherwise */}
          <div
            style={{
              width: fhd(176),
              height: fhd(56),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: focused ? colors.accent : colors.bgSurface,
              color: focused ? colors.textOnAccent : colors.textSecondary,
              fontSize: fhd(28),
              fontWeight: 600,
              transition: 'background-color 120ms ease, color 120ms ease',
            }}
          >
            View
          </div>
          {items.length > 1 && (
            <div style={{ display: 'flex', gap: fhd(12) }}>
              {items.map((dotItem, i) => (
                <button
                  key={dotItem.idBC}
                  type="button"
                  aria-label={`Show spotlight item ${i + 1}`}
                  onClick={() => setIdx(i)}
                  style={{
                    width: fhd(14),
                    height: fhd(14),
                    borderRadius: '50%',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    backgroundColor: i === idx ? colors.accent : colors.textSecondary,
                    opacity: i === idx ? 1 : 0.45,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
